import { useState, useEffect, useMemo, type CSSProperties } from "react";
import SectionErrorBoundary from "@/components/SectionErrorBoundary";
import HomePageSkeleton from "@/components/skeletons/HomePageSkeleton";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getProductImageUrl } from "@/hooks/useCatalog";
import BottomNav from "@/components/BottomNav";
import InstallGuideModal, { isIosSafari, isStandalone } from "@/components/InstallGuideModal";
import AiRestoredModal from "@/components/AiRestoredModal";
import { FlaskConical } from "lucide-react";
import DesktopSidebar from "@/components/DesktopSidebar";
import { useAuth } from "@/contexts/AuthContext";
import { useSessionTracker } from "@/hooks/useSession";
import { buildRecipeRoute } from "@/lib/recipeRoute";
import { useTranslation } from "react-i18next";
import { useLocale } from "@/contexts/LocaleContext";
import { getLocalizedRecipesByIds } from "@/lib/localizedRecipes";
import { resolveRecipeImageFull, hasRealImage } from "@/lib/resolveRecipeImage";
import { getCorePriority } from "@/lib/contentPriority";
import { getCollectionPath } from "@/lib/collectionSlugs";
import { useLocalizedRecipes } from "@/hooks/useLocalizedRecipes";
import { useScreenTime } from "@/hooks/useScreenTime";
import { buildRecipeCountsByModule, countOwnedRecipeModules } from "@/lib/ownedContentCounts";
import s from "./Inicio.module.css";

const formatTime = (time?: string | null) => {
  if (!time) return null;
  return time.replace(/\s*minutos?/i, " min").replace(/\s*minutes?/i, " min");
};

const GLUCOPHOTO_IDS = ["7330458", "7468894", "7487694"];

type OwnedContentItem = {
  id: string;
  label: string;
  imageUrl: string | null;
  imageUrlMobile: string | null;
  path: string;
  recipeCount: number;
  flag: string | null;
};

function getTimeGreeting(): { key: string } {
  const hour = new Date().getHours();
  if (hour < 12) return { key: "home.good_morning" };
  if (hour < 17) return { key: "home.good_afternoon" };
  return { key: "home.good_evening" };
}

// ── Language flag per product, per the market mapping — a module's flag is
// about which MARKET it was sold in, independent of the app's current
// display language (a Spanish-speaking user who owns Clean Bakes still
// sees the US flag on that card). ──
const US_PRODUCT_IDS = new Set(["7381203", "7381661", "7456362", "8061842", "8062101", "8073539"]);
const MX_PRODUCT_IDS = new Set(["7368845", "7222660", "7224807", "7339562", "7368977", "7418452", "8131891"]);
const NO_FLAG_PRODUCT_IDS = new Set(["7487694"]); // Glucofy AI
const DE_NAME_PATTERN = /brot|koch|k[uü]chen|gl[uü]ck/i;

function getProductFlag(hotmartProductId: string, name: string): string | null {
  if (NO_FLAG_PRODUCT_IDS.has(hotmartProductId)) return null;
  if (US_PRODUCT_IDS.has(hotmartProductId)) return "🇺🇸";
  if (MX_PRODUCT_IDS.has(hotmartProductId)) return "🇲🇽";
  if (DE_NAME_PATTERN.test(name)) return "🇩🇪";
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────

const Inicio = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, hasProduct, testerMode, setTesterMode, setTesterProducts, setTesterSubscriptionStatus, refreshProfile } = useAuth();
  useScreenTime("home");
  // Home renders its own page shell (sidebar + main), not AppLayout — AppLayout's
  // shared <main> applies md:max-w-4xl + md:mx-auto on top of its own sidebar-flex
  // row, which recenters content instead of filling it. Every other screen keeps
  // using AppLayout unchanged.
  useSessionTracker();
  const isAdmin = user?.isAdmin ?? false;
  const { language } = useLocale();
  const displayName = user?.display_name || user?.email?.split("@")[0] || "";
  const greeting = getTimeGreeting();
  const [refreshingAccess, setRefreshingAccess] = useState(false);
  const [showInstallGuide, setShowInstallGuide] = useState(false);
  const [showAiRestored, setShowAiRestored] = useState(false);

  // ── Auto-show install guide for iOS Safari users without the PWA ──────────
  useEffect(() => {
    if (isIosSafari() && !isStandalone()) {
      if (localStorage.getItem("install_guide_shown") === "true") return;
      const timer = setTimeout(() => {
        setShowInstallGuide(true);
        localStorage.setItem("install_guide_shown", "true");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  // ── One-time "AI features restored" notice ─────────────────────────────────
  useEffect(() => {
    if (!user?.id) return;
    const resolvedKey = `onboarding_fully_resolved_v1_${user.id}`;

    const maybeShow = () => {
      if (localStorage.getItem(resolvedKey) !== "true") return false;
      if (localStorage.getItem("ai_restored_v1_shown") !== "true") {
        setShowAiRestored(true);
      }
      return true;
    };

    if (maybeShow()) return;
    const interval = setInterval(() => {
      if (maybeShow()) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [user?.id]);

  const handleAiRestoredOpenChange = (open: boolean) => {
    setShowAiRestored(open);
    if (!open) {
      localStorage.setItem("ai_restored_v1_shown", "true");
    }
  };

  // ── Data: recipe modules ───────────────────────────────────────────────────
  const { data: recipeOfertas } = useQuery({
    queryKey: ["recipe-modules-home", language],
    queryFn: async () => {
      const { data } = await supabase
        .from("recipe_modules")
        .select("*")
        .eq("active", true)
        .order("sort_order");
      return data || [];
    },
    staleTime: 1000 * 60 * 60,
  });

  // ── Data: recipe counts per module (for "Your content" cards) ──────────────
  // A dedicated, paginated count query, kept separate from rawAllRecipes below
  // (and from the module grid render) so the "Your content" cards paint
  // immediately with a skeleton pill instead of waiting on this to resolve —
  // rawAllRecipes is also capped at PostgREST's default 1000-row limit (no
  // .range()), which would silently undercount modules once total recipes
  // exceed that, so it can't be reused for counts either way.
  const { data: recipeCounts, isLoading: countsLoading } = useQuery({
    queryKey: ["recipe-counts-by-module"],
    queryFn: async () => {
      const counts: Record<string, number> = {};
      const batchSize = 1000;
      for (let from = 0; ; from += batchSize) {
        const { data, error } = await supabase
          .from("recipes")
          .select("module_id")
          .not("module_id", "is", null)
          .order("id")
          .range(from, from + batchSize - 1);
        if (error) throw error;
        if (!data?.length) break;
        for (const r of data) {
          if (r.module_id) counts[r.module_id] = (counts[r.module_id] || 0) + 1;
        }
        if (data.length < batchSize) break;
      }
      return counts;
    },
    staleTime: 1000 * 60 * 10,
  });

  // ── Data: all recipes ──────────────────────────────────────────────────────
  const rawAllRecipes = useLocalizedRecipes(
    useQuery({
      queryKey: ["all-recipes-home"],
      queryFn: async () => {
        const { data } = await supabase.from("recipes")
          .select("id,title,image,image_group,category,total_time,time,prep_time,semaforo,module_id,language,sort_order,tags,meal_moment,difficulty,demand_score")
          .order("demand_score", { ascending: false })
          .order("sort_order");
        return data || [];
      },
      staleTime: 30 * 60 * 1000,
      gcTime: 60 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: true,
    }).data,
  );

  // ── Data: recently viewed ──────────────────────────────────────────────────
  const { data: recentlyViewed } = useQuery({
    queryKey: ["recently-viewed-recipes", user?.id, language],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data: history } = await supabase
        .from("recipe_view_history")
        .select("recipe_id, last_viewed_at")
        .eq("user_id", user.id)
        .order("last_viewed_at", { ascending: false })
        .limit(10);
      if (!history || history.length === 0) return [];
      const ids = history.map((h) => h.recipe_id);
      return await getLocalizedRecipesByIds(ids, language);
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // ── Derived: recipes with images ──────────────────────────────────────────
  const allWithImages = useMemo(() => (rawAllRecipes || []).filter(hasRealImage), [rawAllRecipes]);

  const recipeCountsByModule = useMemo(
    () => buildRecipeCountsByModule(rawAllRecipes || []),
    [rawAllRecipes],
  );

  const ownedCoreModuleCount = useMemo(
    () =>
      countOwnedRecipeModules({
        recipeModules: recipeOfertas || [],
        recipeCountsByModule,
        hasProduct,
        excludedProductIds: GLUCOPHOTO_IDS,
      }),
    [recipeOfertas, recipeCountsByModule, hasProduct],
  );

  // ── Recommended recipes ────────────────────────────────────────────────────
  const recommendedRecipes = useMemo(() => {
    const viewedIds = new Set((recentlyViewed || []).map((r: any) => r.id));
    const candidates = allWithImages
      .filter((r: any) => !viewedIds.has(r.id))
      .filter((r: any) => !r.language || r.language === language);
    return [...candidates].sort((a: any, b: any) => (b.demand_score ?? 50) - (a.demand_score ?? 50)).slice(0, 8);
  }, [allWithImages, recentlyViewed, language]);

  // ── Owned content ("Your content") ─────────────────────────────────────────
  const ownedContentItems = useMemo(() => {
    if (!recipeOfertas) return [];
    const items: OwnedContentItem[] = [];

    const resolveImg = (img: string | null | undefined) =>
      img ? (img.startsWith("http") ? img : getProductImageUrl(img)) : null;

    // Each hotmart_product_id is its own independent offer — Panadería Cero,
    // Clean Bakes and Brotglück are 3 different products, never grouped
    // together. Within a single product, its ES/EN/DE recipe_modules rows
    // are language variants of the SAME offer, so only one is shown, picked
    // by the user's current language (falling back to ES, then EN).
    const ownedModules = (recipeOfertas || [])
      .filter((m: any) => m.hotmart_product_id && hasProduct(m.hotmart_product_id))
      .filter((m: any) => !GLUCOPHOTO_IDS.includes(m.hotmart_product_id));

    const modulesByProduct = new Map<string, any[]>();
    for (const mod of ownedModules) {
      const list = modulesByProduct.get(mod.hotmart_product_id) || [];
      list.push(mod);
      modulesByProduct.set(mod.hotmart_product_id, list);
    }

    const pickVariant = (mods: any[]) =>
      mods.find((m) => m.language === language) ||
      mods.find((m) => m.language === "es") ||
      mods.find((m) => m.language === "en") ||
      mods[0];

    const selectedModules = Array.from(modulesByProduct.values())
      .map(pickVariant)
      .sort((a: any, b: any) => getCorePriority(a.hotmart_product_id) - getCorePriority(b.hotmart_product_id));

    for (const mod of selectedModules) {
      items.push({
        id: mod.id,
        label: mod.name,
        imageUrl: resolveImg(mod.image),
        imageUrlMobile: resolveImg(mod.image_mobile) || resolveImg(mod.image),
        path: getCollectionPath(mod.id, mod.name),
        recipeCount: recipeCounts?.[mod.id] || 0,
        flag: getProductFlag(mod.hotmart_product_id, mod.name),
      });
    }

    // La Botica de la Abuela isn't a recipe_modules row — added manually.
    // (Clean Remedies IS a recipe_modules row, already included above.)
    if (hasProduct("8131891")) {
      items.push({
        id: "la-botica",
        label: "La Botica de la Abuela",
        imageUrl: null,
        imageUrlMobile: null,
        path: "/la-botica",
        recipeCount: 0,
        flag: getProductFlag("8131891", "La Botica de la Abuela"),
      });
    }

    return items;
  }, [recipeOfertas, hasProduct, language, recipeCounts]);

  const isHomeLoading = !rawAllRecipes || !recipeOfertas;

  return (
    <>
      <div className={s.homeShell}>
        <DesktopSidebar />

        <main className={s.homeMain}>
          <div className={s.homeContainer}>
            {isHomeLoading ? (
              <HomePageSkeleton />
            ) : (
              <>
                {/* ── Greeting ── */}
                <section className={s.homeTop}>
                  <h1 className={s.homeGreeting}>{t(greeting.key, { defaultValue: "Hello" })}, {displayName || user?.email?.split("@")[0] || ""} 👋</h1>
                </section>

                {/* ══════════════════════════════════════
                    Your content — front and center: what the customer
                    bought and how to open it, readable in one glance.
                ══════════════════════════════════════ */}
                {ownedContentItems.length > 0 && (
                  <SectionErrorBoundary section="my_content" blockType="recipe" compact>
                    <section className={s.homeSection}>
                      <div className={s.homeSectionHead}>
                        <div>
                          <h2 className={s.homeSectionTitle}>{t("home.your_contents")}</h2>
                          <div className={s.homeSectionSubtitle}>{t("home.your_contents_desc", "Open any collection included in your account.")}</div>
                        </div>
                        {ownedContentItems.length > 4 && (
                          <button onClick={() => navigate("/my-content")} className={s.homeViewAll}>{t("recipes.view_all", "View all")} →</button>
                        )}
                      </div>

                      <div className={s.homeCollections}>
                        {ownedContentItems.map((item) => (
                          <article key={item.id} className={s.homeCollectionCard}>
                            <div
                              className={s.homeCollectionImage}
                              style={{
                                ...(item.imageUrl ? { "--product-img-desktop": `url('${item.imageUrl}')` } : {}),
                                ...(item.imageUrlMobile ? { "--product-img-mobile": `url('${item.imageUrlMobile}')` } : {}),
                              } as CSSProperties}
                            >
                              {item.flag && <div className={s.homeFlag}>{item.flag}</div>}
                            </div>
                            <div className={s.homeCollectionBody}>
                              <div className={s.homeCollectionTitle}>{item.label}</div>
                              {countsLoading ? (
                                <span className={`${s.homeRecipePill} ${s.homeRecipePillSkeleton}`} />
                              ) : item.recipeCount > 0 ? (
                                <span className={s.homeRecipePill}>{item.recipeCount} {t("home.recipes_count_label", "recipes")}</span>
                              ) : null}
                              <button onClick={() => navigate(item.path)} className={s.homeAccessButton}>
                                {t("home.access_recipes", "Access recipes")} →
                              </button>
                            </div>
                          </article>
                        ))}
                      </div>
                    </section>
                  </SectionErrorBoundary>
                )}

                {/* ── No-access banner (only for users with 0 content) ── */}
                {ownedCoreModuleCount === 0 && (
                  <div className="mb-6 px-1">
                    <div className="px-4 py-3 rounded-2xl flex items-center justify-between gap-3 animate-fade-in bg-white border border-[#eadfcd]">
                      <div className="min-w-0">
                        <p className="text-sm font-bold" style={{ color: "#1f241f" }}>{t("home.no_access_title")}</p>
                        <p className="text-xs mt-0.5" style={{ color: "#756b5d" }}>{t("home.no_access_desc")}</p>
                      </div>
                      <button
                        onClick={async () => { setRefreshingAccess(true); await refreshProfile(); setRefreshingAccess(false); }}
                        disabled={refreshingAccess}
                        className="shrink-0 px-3 py-2 rounded-xl text-white text-xs font-bold disabled:opacity-50 active:scale-95 transition-all"
                        style={{ backgroundColor: "#5f8a59" }}
                      >
                        {refreshingAccess ? "..." : t("home.no_access_refresh")}
                      </button>
                    </div>
                  </div>
                )}

                {/* ══════════════════════════════════════
                    Continue where you left off
                    (no progress bar — no real per-recipe progress data exists)
                ══════════════════════════════════════ */}
                {recentlyViewed && recentlyViewed.length > 0 && (
                  <SectionErrorBoundary section="continue_watching" blockType="recipe" compact>
                    <section className={s.homeSection} id="continue-section">
                      <div className={s.homeSectionHead}>
                        <div>
                          <h2 className={s.homeSectionTitle}>{t("home.continue_where_left")}</h2>
                          <div className={s.homeSectionSubtitle}>{t("home.continue_where_left_desc", "Quick access to recipes you recently opened.")}</div>
                        </div>
                        <button onClick={() => navigate("/my-content")} className={s.homeViewAll}>{t("recipes.view_all", "View all")} →</button>
                      </div>

                      <div className={s.homeRecipeRow}>
                        {[...recentlyViewed]
                          .sort((a: any, b: any) => (hasRealImage(b) ? 1 : 0) - (hasRealImage(a) ? 1 : 0))
                          .slice(0, 5)
                          .map((recipe: any) => {
                            const img = resolveRecipeImageFull(recipe.image, recipe.image_group, recipe.category).url;
                            const time = formatTime(recipe.total_time || recipe.time || recipe.prep_time);
                            return (
                              <button key={recipe.id} onClick={() => navigate(buildRecipeRoute(recipe))} className={s.homeMiniRecipe}>
                                <div className={s.homeMiniImage} style={img ? { backgroundImage: `url('${img}')` } : undefined}>
                                  {time && <span className={s.homeTime}>{time}</span>}
                                </div>
                                <div className={s.homeMiniTitle}>{recipe.title}</div>
                              </button>
                            );
                          })}
                      </div>
                    </section>
                  </SectionErrorBoundary>
                )}

                {/* ══════════════════════════════════════
                    Recommended for you
                ══════════════════════════════════════ */}
                {recommendedRecipes.length > 0 && (
                  <SectionErrorBoundary section="recommended" blockType="recipe" compact>
                    <section className={s.homeSection}>
                      <div className={s.homeSectionHead}>
                        <div>
                          <h2 className={s.homeSectionTitle}>{t("home.recommended_for_you")}</h2>
                          <div className={s.homeSectionSubtitle}>{t("home.recommended_for_you_desc", "Simple recipes selected for today.")}</div>
                        </div>
                        <button onClick={() => navigate("/recipes")} className={s.homeViewAll}>{t("recipes.view_all", "View all")} →</button>
                      </div>

                      <div className={s.homeRecipeRow}>
                        {recommendedRecipes.slice(0, 5).map((recipe: any) => {
                          const img = resolveRecipeImageFull(recipe.image, recipe.image_group, recipe.category).url;
                          const time = formatTime(recipe.total_time || recipe.time || recipe.prep_time);
                          return (
                            <button key={recipe.id} onClick={() => navigate(buildRecipeRoute(recipe))} className={s.homeMiniRecipe}>
                              <div className={s.homeMiniImage} style={img ? { backgroundImage: `url('${img}')` } : undefined}>
                                {time && <span className={s.homeTime}>{time}</span>}
                              </div>
                              <div className={s.homeMiniTitle}>{recipe.title}</div>
                            </button>
                          );
                        })}
                      </div>
                    </section>
                  </SectionErrorBoundary>
                )}

                {/* ── Admin shortcut ── */}
                {isAdmin && !testerMode && (
                  <div className="mb-4">
                    <button
                      onClick={() => navigate("/admin")}
                      className="w-full min-h-[52px] rounded-2xl text-base font-medium text-center active:scale-[0.97] transition-transform bg-white border"
                      style={{ borderColor: "#eadfcd", color: "#1f241f" }}
                    >
                      ⚙️ {t("nav.admin")}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      {testerMode && (
        <button
          onClick={() => { setTesterMode(false); setTesterProducts([]); setTesterSubscriptionStatus(null); }}
          className="fixed top-3 right-3 z-50 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white text-[11px] font-bold animate-pulse hover:animate-none active:scale-95 transition-transform"
          style={{ backgroundColor: "#B8966A", boxShadow: "0 4px 12px -2px #B8966A55" }}
        >
          <FlaskConical size={12} />
          {t("tester.title")}
        </button>
      )}

      <BottomNav />
      <InstallGuideModal open={showInstallGuide} onOpenChange={setShowInstallGuide} />
      <AiRestoredModal open={showAiRestored} onOpenChange={handleAiRestoredOpenChange} />
    </>
  );
};

export default Inicio;
